<script setup lang="ts">
import imgHandLettingMicDrop from '@/assets/hand-dropping-mic.png';
import imgSpain from '@/assets/spain.png';
import imgEnglish from '@/assets/usa-freedom.png';
import imgGerman from '@/assets/german-.png';
import imgFrench from '@/assets/french-with-baguette.png';
import imgGlobe from '@/assets/globe-.png';
import { Book, Zap, ArrowRight, Plus, Check } from 'lucide-vue-next';
import imgBooks from '@/assets/books.png';
import imgThumbsUp from '@/assets/thumbs-up.png';
import ChatSimulator from '@/components/ChatSimulator.vue';
import imgGirlWithHeadset from '@/assets/girl-with-headset.png';
import imgHotGirlWithAHeadset from '@/assets/hot-girl-with-a-headset.png';
import imgCatWearingAGamingHeadset from '@/assets/cat-wearing-a-gaming-headset.png';
import FeedbackWidget from '../components/FeedbackWidget.vue';
import { useTranslations } from '../composables/useTranslations';
import { computed } from 'vue';
import { currentLanguage, getLocalizedPath } from '../composables/useLanguage';

const t = useTranslations();

const testimonials = computed(() => [
  {
    quote: t.value.afterReturningFromSpainIStruggled,
    author: 'Sarah M.',
    role: t.value.returnedExchangeStudent,
    image: imgGirlWithHeadset,
  },
  {
    quote: t.value.iCompletedAllOfDuolingo,
    author: 'Michael K.',
    role: t.value.languageEnthusiast,
    image: imgHotGirlWithAHeadset,
  },
  {
    quote: t.value.asABusyProfessionalINeededFlexiblePracticeTimes,
    author: 'David L.',
    role: t.value.businessProfessional,
    image: imgCatWearingAGamingHeadset,
  },
]);
</script>

<template>
  <div class="flex flex-col items-center bg-bg-primary">
    <!-- Hero Section -->
    <section
      id="hero"
      class="w-full relative overflow-hidden bg-[#f8faff] lg:mt-[-56px]"
    >
      <div class="absolute inset-0">
        <div
          class="absolute inset-0 bg-linear-to-b from-[#f8faff] via-[#f0f5ff] to-[#f8faff] rounded-[50%] scale-[1.5] translate-y-[30%]"
        />
      </div>
      <div
        class="mx-auto py-4 md:py-10 lg:h-screen lg:flex lg:items-center relative container"
      >
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div class="lg:col-span-7 space-y-6">
            <div class="space-y-6">
              <div class="flex items-center justify-between">
                <h2 class="max-w-96 whitespace-nowrap sm:max-w-full">
                  <div
                    class="font-bold text-[clamp(2rem,10vw,3.6rem)] leading-none"
                  >
                    {{ t.learnLanguages }}
                  </div>
                  <div
                    class="font-black text-[clamp(2rem,10vw,3.6rem)] leading-none"
                  >
                    <span v-if="currentLanguage !== 'es'" class="mr-3">
                      {{ t.through }}
                    </span>
                    <span class="underline underline-offset-16">
                      {{ t.speaking }}
                    </span>
                  </div>
                </h2>
                <img
                  :src="imgHandLettingMicDrop"
                  alt="Diverse group of friends smiling together"
                  class="hidden size-18 object-contain"
                  loading="lazy"
                  fetchpriority="low"
                />
              </div>
              <p class="text-xl text-black/60">
                {{ t.connectWithNativeSpeakersEngage }}
              </p>
            </div>
            <div class="flex flex-col gap-0">
              <h3 class="text-[26px] leading-tight font-semibold mb-4 mt-5">
                {{ t.whichLanguageDoYouWantToSpeak }}
              </h3>
              <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-4">
                <!-- Spanish Card -->
                <RouterLink
                  :to="getLocalizedPath('/rooms?language=spanish')"
                  class="max-h-44 group relative bg-gradient-to-br from-[#bb3b0e] to-[#c85a34] p-6 rounded-2xl hover:scale-105 transition-all duration-300"
                >
                  <div class="relative flex flex-col items-center gap-4">
                    <img
                      :src="imgSpain"
                      alt="Spanish"
                      class="size-16 object-contain group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                      fetchpriority="low"
                    />
                    <div class="text-white">
                      <div class="font-bold text-xl">{{ t.Spanish }}</div>
                      <div class="text-white/80 text-sm">¡Vamos a hablar!</div>
                    </div>
                  </div>
                </RouterLink>

                <!-- English Card -->
                <RouterLink
                  :to="getLocalizedPath('/rooms?language=english')"
                  class="max-h-44 group relative bg-gradient-to-br from-[#dd7631] to-[#e19058] p-6 rounded-2xl hover:scale-105 transition-all duration-300"
                >
                  <div class="relative flex flex-col items-center gap-4">
                    <img
                      :src="imgEnglish"
                      alt="English"
                      class="size-16 object-contain group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                      fetchpriority="low"
                    />
                    <div class="text-white">
                      <div class="font-bold text-xl">{{ t.English }}</div>
                      <div class="text-white/80 text-sm">Let's chat!</div>
                    </div>
                  </div>
                </RouterLink>

                <!-- German Card -->
                <RouterLink
                  :to="getLocalizedPath('/rooms?language=german')"
                  class="max-h-44 group relative bg-gradient-to-br from-[#708160] to-[#879775] p-6 rounded-2xl hover:scale-105 transition-all duration-300"
                >
                  <div class="relative flex flex-col items-center gap-4">
                    <img
                      :src="imgGerman"
                      alt="German"
                      class="size-16 object-contain group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                      fetchpriority="low"
                    />
                    <div class="text-white">
                      <div class="font-bold text-xl">{{ t.German }}</div>
                      <div class="text-white/80 text-sm">Lass uns reden!</div>
                    </div>
                  </div>
                </RouterLink>

                <!-- French Card -->
                <RouterLink
                  :to="getLocalizedPath('/rooms?language=french')"
                  class="max-h-44 group relative bg-gradient-to-br from-[#d8c593] to-[#e0d1a9] p-6 rounded-2xl hover:scale-105 transition-all duration-300"
                >
                  <div class="relative flex flex-col items-center gap-4">
                    <img
                      :src="imgFrench"
                      alt="French"
                      class="size-16 object-contain group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                      fetchpriority="low"
                    />
                    <div class="text-white">
                      <div class="font-bold text-xl">French</div>
                      <div class="text-white/80 text-sm">Parlons-en!</div>
                    </div>
                  </div>
                </RouterLink>
              </div>

              <!-- Global Languages Card -->
              <RouterLink
                :to="getLocalizedPath('/rooms')"
                class="group relative bg-gradient-to-r from-[#d87c5b] to-[#708160] p-6 rounded-2xl hover:scale-[1.02] transition-all duration-300 mt-4"
              >
                <div class="relative flex items-center justify-between">
                  <div class="text-white">
                    <div class="font-bold text-2xl mb-1 text-left">
                      {{ t.exploreMoreLanguages }}
                    </div>
                    <div class="text-white/80 text-left">
                      {{ t.joinSpeakersFromOver }}
                    </div>
                  </div>
                  <img
                    :src="imgGlobe"
                    alt="Global Languages"
                    class="w-20 h-20 object-contain group-hover:rotate-12 transition-transform duration-500 sm:mr-10"
                    loading="lazy"
                    fetchpriority="low"
                  />
                </div>
              </RouterLink>
            </div>
          </div>
          <div class="lg:col-span-5">
            <div class="space-y-8">
              <ChatSimulator />

              <!-- Social Proof Stats -->
              <div class="grid grid-cols-2 gap-6">
                <div
                  class="text-center p-4 rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm"
                >
                  <div
                    class="text-3xl font-bold bg-accent bg-clip-text text-transparent"
                  >
                    20+
                  </div>
                  <div class="text-secondary mt-1 font-medium">
                    {{ t.languagesAvailable }}
                  </div>
                </div>
                <div
                  class="text-center p-4 rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm"
                >
                  <div
                    class="text-3xl font-bold bg-accent bg-clip-text text-transparent"
                  >
                    24/7
                  </div>
                  <div class="text-secondary mt-1 font-medium">
                    {{ t.practiceAnytime }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Language Rooms Works Better Section -->
    <section id="why-it-works" class="w-full py-24 bg-[#f8faff]">
      <div class="container mx-auto">
        <div class="text-center mb-16">
          <div class="flex flex-col items-center">
            <h2 class="text-3xl md:text-4xl font-bold mb-6">
              <span class="text-[#bb3b0e]">{{ t.improve }}</span>
              {{ t.yourLanguageSkills }}
            </h2>
            <p class="text-xl text-secondary max-w-2xl mx-auto">
              {{ t.combineStructuredLearningWithRealWorldSpeakingPractice }}
            </p>
          </div>
        </div>

        <div class="space-y-8">
          <!-- Story Cards -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- Traditional Learning Story -->
            <div
              class="bg-white rounded-2xl overflow-hidden shadow-lg group relative"
            >
              <img
                :src="imgBooks"
                alt="Traditional textbook learning"
                class="absolute top-4 right-4 w-16 h-16 object-contain"
                loading="lazy"
                fetchpriority="low"
              />
              <div class="p-8">
                <div class="flex items-center gap-4 mb-6">
                  <div
                    class="w-12 h-12 rounded-xl bg-[#bb3b0e]/10 flex items-center justify-center"
                  >
                    <Book class="w-6 h-6 text-[#bb3b0e]" />
                  </div>
                  <h3 class="text-xl font-bold text-primary">
                    {{ t.traditionalLearning }}
                  </h3>
                </div>
                <div class="space-y-6">
                  <div class="flex items-center gap-3">
                    <div class="text-4xl font-black text-[#bb3b0e]">
                      <Check class="w-10 h-10" stroke-width="3" />
                    </div>
                    <div class="text-xl font-bold text-primary">
                      {{ t.strongFoundation }}
                    </div>
                  </div>
                  <div class="space-y-4">
                    <div class="flex items-start gap-3">
                      <div
                        class="w-6 h-6 rounded-full bg-[#bb3b0e]/10 flex items-center justify-center mt-1 shrink-0"
                      >
                        <div class="w-2 h-2 rounded-full bg-[#bb3b0e]" />
                      </div>
                      <p class="text-secondary">
                        {{ t.structuredGrammarAndVocabularyLearning }}
                      </p>
                    </div>
                    <div class="flex items-start gap-3">
                      <div
                        class="w-6 h-6 rounded-full bg-[#bb3b0e]/10 flex items-center justify-center mt-1 shrink-0"
                      >
                        <div class="w-2 h-2 rounded-full bg-[#bb3b0e]" />
                      </div>
                      <p class="text-secondary">
                        {{ t.systematicProgressionThroughLanguageLevels }}
                      </p>
                    </div>
                    <div class="flex items-start gap-3">
                      <div
                        class="w-6 h-6 rounded-full bg-[#bb3b0e]/10 flex items-center justify-center mt-1 shrink-0"
                      >
                        <div class="w-2 h-2 rounded-full bg-[#bb3b0e]" />
                      </div>
                      <p class="text-secondary">
                        {{ t.professionalGuidanceAndFeedback }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Language Rooms Story -->
            <div
              class="bg-gradient-to-br from-[#bb3b0e] to-[#dd7631] rounded-2xl overflow-hidden shadow-lg group relative"
            >
              <img
                :src="imgThumbsUp"
                alt="Approved method"
                class="absolute top-4 right-4 w-16 h-16 object-contain"
                loading="lazy"
                fetchpriority="low"
              />
              <div class="p-8 relative">
                <div class="flex items-center gap-4 mb-6">
                  <div
                    class="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center"
                  >
                    <Zap class="w-6 h-6 text-white" />
                  </div>
                  <h3 class="text-xl font-bold text-white">Language Rooms</h3>
                </div>
                <div class="space-y-6">
                  <div class="flex items-center gap-3">
                    <div class="text-4xl font-black text-white">
                      <Plus class="w-10 h-10" stroke-width="3" />
                    </div>
                    <div class="text-xl font-bold text-white">
                      {{ t.realWorldSkills }}
                    </div>
                  </div>
                  <div class="space-y-4">
                    <div class="flex items-start gap-3">
                      <div
                        class="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mt-1 shrink-0"
                      >
                        <div class="w-2 h-2 rounded-full bg-white" />
                      </div>
                      <p class="text-white/90">
                        {{ t.dailyConversationsWithNativeSpeakers }}
                      </p>
                    </div>
                    <div class="flex items-start gap-3">
                      <div
                        class="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mt-1 shrink-0"
                      >
                        <div class="w-2 h-2 rounded-full bg-white" />
                      </div>
                      <p class="text-white/90">
                        {{ t.naturalLanguagePatternsAndExpressions }}
                      </p>
                    </div>
                    <div class="flex items-start gap-3">
                      <div
                        class="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mt-1 shrink-0"
                      >
                        <div class="w-2 h-2 rounded-full bg-white" />
                      </div>
                      <p class="text-white/90">
                        {{ t.buildConfidenceThroughRegularPractice }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Key Benefits -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div class="bg-white p-6 rounded-2xl shadow-lg">
              <div class="flex items-center gap-3 mb-4">
                <div
                  class="w-10 h-10 rounded-xl bg-[#bb3b0e]/10 flex items-center justify-center"
                >
                  <Zap class="w-5 h-5 text-[#bb3b0e]" />
                </div>
                <h4 class="font-semibold">{{ t.immediatePractice }}</h4>
              </div>
              <p class="text-secondary">
                {{ t.startSpeakingFromDayOneNoMoreSilentStudying }}
              </p>
            </div>

            <div class="bg-white p-6 rounded-2xl shadow-lg">
              <div class="flex items-center gap-3 mb-4">
                <div
                  class="w-10 h-10 rounded-xl bg-[#bb3b0e]/10 flex items-center justify-center"
                >
                  <Zap class="w-5 h-5 text-[#bb3b0e]" />
                </div>
                <h4 class="font-semibold">{{ t.nativeSpeakers }}</h4>
              </div>
              <p class="text-secondary">
                {{ t.learnHowLocalsReallyTalkNotJustTextbookPhrases }}
              </p>
            </div>

            <div class="bg-white p-6 rounded-2xl shadow-lg">
              <div class="flex items-center gap-3 mb-4">
                <div
                  class="w-10 h-10 rounded-xl bg-[#bb3b0e]/10 flex items-center justify-center"
                >
                  <Zap class="w-5 h-5 text-[#bb3b0e]" />
                </div>
                <h4 class="font-semibold">{{ t.realResults }}</h4>
              </div>
              <p class="text-secondary">
                {{ t.buildGenuineConfidenceThroughActualConversations }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="w-full py-24 bg-gradient-to-b from-white to-bgSecondary">
      <div class="container mx-auto">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">
            {{ t.realStoriesRealProgress }}
          </h2>
          <p class="text-xl text-secondary max-w-2xl mx-auto">
            {{ t.joinThousandsOfLanguageLearners }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div
            v-for="(testimonial, index) in testimonials"
            :key="index"
            class="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 relative group"
          >
            <div class="relative">
              <!-- Quote -->
              <p class="text-gray-600 mb-6 relative">
                {{ testimonial.quote }}
              </p>

              <!-- Author -->
              <div class="flex items-center gap-4">
                <img
                  :src="testimonial.image"
                  :alt="testimonial.author"
                  class="w-12 h-12 rounded-full object-cover"
                  loading="lazy"
                  fetchpriority="low"
                />
                <div>
                  <div class="font-semibold text-gray-900">
                    {{ testimonial.author }}
                  </div>
                  <div class="text-sm text-gray-500">
                    {{ testimonial.role }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Additional Social Proof -->
        <div class="mt-16 text-center">
          <p class="text-lg text-secondary mb-8">
            {{ t.joinLearnersFromOver }}
            <span class="font-semibold text-accent">20+ {{ t.countries }}</span>
            {{ t.whoAreAlreadyImprovingTheirLanguageSkills }}
          </p>
        </div>
      </div>
    </section>

    <!-- Call to Action Section -->
    <section class="w-full mb-24">
      <div class="container mx-auto text-center">
        <h2 class="text-3xl md:text-4xl font-bold text-primary mb-6">
          {{ t.readyToStartSpeaking }}
        </h2>
        <p class="text-xl text-secondary mb-12 max-w-2xl mx-auto">
          {{ t.joinARoomAndStartPracticing }}
        </p>
        <RouterLink
          :to="getLocalizedPath('/rooms')"
          class="inline-flex items-center px-8 py-4 rounded-xl bg-accent hover:bg-accent/95 transition-all duration-200 shadow-lg text-lg font-medium text-white group"
        >
          {{ t.findARoomNow }}
          <ArrowRight
            class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform"
          />
        </RouterLink>
      </div>
    </section>
    <FeedbackWidget type="fab" />
  </div>
</template>
